/**
 * SEO Service for LogCrest Production Website (www.logcrest.com)
 * Comprehensive SEO optimization for Google search engine visibility
 */

// SEO Configuration for Production
const SEO_CONFIG = {
  baseUrl: 'https://www.logcrest.com',
  siteName: 'LogCrest',
  defaultTitle: 'LogCrest - AI-Powered Observability Platform | Log Management & DevOps Monitoring',
  defaultDescription: 'LogCrest is the leading AI-powered observability platform for logs, infrastructure monitoring, and intelligent alerting. Reduce costs by 50% and improve system reliability.',
  defaultKeywords: 'log management platform, observability platform, AI observability tool, cloud monitoring solution, log analytics software, DevOps monitoring tools, infrastructure monitoring, application performance monitoring, centralized logging system, real time log analysis, distributed tracing tool, cloud native observability, kubernetes log monitoring, microservices monitoring, system observability platform',
  author: 'LogCrest Team',
  twitterHandle: '@LogCrest',
  facebookAppId: 'your-facebook-app-id', // Replace with actual Facebook App ID
  googleAnalyticsId: 'G-XXXXXXXXXX', // Replace with actual GA4 ID
  googleTagManagerId: 'GTM-XXXXXXX', // Replace with actual GTM ID
  googleSearchConsoleId: 'your-search-console-id', // Replace with actual Search Console ID
  organization: {
    name: 'LogCrest',
    url: 'https://www.logcrest.com',
    logo: 'https://www.logcrest.com/logo.png',
    description: 'AI-powered observability platform for modern infrastructure'
  }
};

// Page-specific SEO data
const PAGE_SEO_DATA = {
  '/': {
    title: 'AI-Powered Log Management & Observability Platform | LogCrest',
    description: 'LogCrest is an AI-powered observability and log management platform for cloud-native and DevOps teams. Real-time log analysis, infrastructure monitoring, and intelligent alerting.',
    keywords: 'log management platform, observability platform, AI observability tool, cloud monitoring solution, log analytics software, DevOps monitoring tools, infrastructure monitoring, application performance monitoring, centralized logging system, real time log analysis, distributed tracing tool, cloud native observability, kubernetes log monitoring, microservices monitoring, system observability platform',
    canonical: 'https://www.logcrest.com',
    ogType: 'website',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareCompany',
      name: 'LogCrest',
      url: 'https://www.logcrest.com',
      logo: 'https://www.logcrest.com/logo.png',
      description: 'AI-powered observability platform for modern infrastructure',
      sameAs: [
        'https://www.linkedin.com/company/logcrest'
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+1-XXX-XXX-XXXX',
        contactType: 'customer service',
        areaServed: 'US',
        availableLanguage: 'English'
      }
    }
  },
  '/pricing': {
    title: 'LogCrest Pricing - Affordable Observability Solutions | Plans & Features',
    description: 'Choose the perfect LogCrest plan for your team. Transparent pricing for log management, infrastructure monitoring, and AI-powered insights. Start free today.',
    keywords: 'observability pricing, log management cost, infrastructure monitoring plans, SRE tools pricing, DevOps platform cost',
    canonical: 'https://www.logcrest.com/pricing',
    ogType: 'website'
  },
  '/agent-sre': {
    title: 'Agent SRE - AI-Powered Site Reliability Engineering | LogCrest',
    description: 'Revolutionize your SRE practices with LogCrest\'s AI-powered Agent SRE. Automated incident response, intelligent alerting, and proactive system optimization.',
    keywords: 'agent SRE, site reliability engineering, AI SRE, automated incident response, SRE automation, reliability engineering tools',
    canonical: 'https://www.logcrest.com/agent-sre',
    ogType: 'website'
  },
  '/ai-alerting': {
    title: 'AI Alerting - Intelligent Monitoring & Notification System | LogCrest',
    description: 'Reduce alert fatigue with LogCrest\'s AI-powered alerting system. Smart notifications, intelligent filtering, and automated incident response.',
    keywords: 'AI alerting, intelligent monitoring, smart notifications, alert fatigue reduction, automated incident response, monitoring alerts',
    canonical: 'https://www.logcrest.com/ai-alerting',
    ogType: 'website'
  },
  '/security': {
    title: 'Security Monitoring - AI-Powered Threat Detection | LogCrest',
    description: 'Protect your infrastructure with LogCrest\'s advanced security monitoring. AI-powered threat detection, compliance monitoring, and security insights.',
    keywords: 'security monitoring, threat detection, compliance monitoring, infrastructure security, security insights, cybersecurity monitoring',
    canonical: 'https://www.logcrest.com/security',
    ogType: 'website'
  },
  '/ai-ops': {
    title: 'AIOps - Artificial Intelligence for IT Operations | LogCrest',
    description: 'Transform IT operations with LogCrest\'s AIOps platform. Automated incident management, predictive analytics, and intelligent system optimization.',
    keywords: 'AIOps, artificial intelligence operations, IT automation, predictive analytics, incident management, operations intelligence',
    canonical: 'https://www.logcrest.com/ai-ops',
    ogType: 'website'
  },
  '/contact': {
    title: 'Contact LogCrest - Get Support & Sales Information',
    description: 'Get in touch with LogCrest for sales inquiries, technical support, or partnership opportunities. Our team is here to help you succeed.',
    keywords: 'contact LogCrest, support, sales, partnership, technical support, customer service',
    canonical: 'https://www.logcrest.com/contact',
    ogType: 'website'
  },
  '/demo': {
    title: 'LogCrest Demo - See AI-Powered Observability in Action',
    description: 'Experience LogCrest\'s AI-powered observability platform with our interactive demo. See how we can transform your monitoring and alerting.',
    keywords: 'LogCrest demo, observability demo, AI monitoring demo, log management demo, infrastructure monitoring demo',
    canonical: 'https://www.logcrest.com/demo',
    ogType: 'website'
  },
  '/about': {
    title: 'About LogCrest - Leading AI-Powered Observability Platform',
    description: 'Learn about LogCrest\'s mission to revolutionize observability with AI. Meet our team and discover how we\'re transforming infrastructure monitoring.',
    keywords: 'about LogCrest, company, team, mission, observability company, AI monitoring company',
    canonical: 'https://www.logcrest.com/about',
    ogType: 'website'
  },
  '/careers': {
    title: 'Careers at LogCrest - Join Our AI Observability Team',
    description: 'Join LogCrest and help build the future of AI-powered observability. Explore open positions and be part of our innovative team.',
    keywords: 'LogCrest careers, jobs, employment, AI company jobs, observability jobs, tech careers',
    canonical: 'https://www.logcrest.com/careers',
    ogType: 'website'
  },
  '/blog': {
    title: 'LogCrest Blog - Insights on AI, Observability & DevOps',
    description: 'Stay updated with the latest insights on AI-powered observability, DevOps best practices, and infrastructure monitoring trends.',
    keywords: 'observability blog, AI blog, DevOps blog, infrastructure monitoring blog, SRE blog, tech insights',
    canonical: 'https://www.logcrest.com/blog',
    ogType: 'website'
  },
  '/apm': {
    title: 'Application Performance Monitoring (APM) - LogCrest',
    description: 'Monitor application performance with LogCrest\'s APM solution. Real-time insights, error tracking, and performance optimization.',
    keywords: 'APM, application performance monitoring, performance tracking, error monitoring, application insights',
    canonical: 'https://www.logcrest.com/apm',
    ogType: 'website'
  },
  '/log-management': {
    title: 'Log Management Platform - Centralized Log Analysis & Monitoring | LogCrest',
    description: 'Centralize and analyze your logs with LogCrest\'s powerful log management platform. Real-time search, analysis, and intelligent insights. Best log management platform for startups and enterprises.',
    keywords: 'log management platform, log management, log analysis, centralized logging system, centralized logging, log monitoring, log search, log insights, real time log analysis, scalable logging system for cloud apps, lightweight log monitoring tool',
    canonical: 'https://www.logcrest.com/log-management',
    ogType: 'website'
  },
  '/observability-platform': {
    title: 'Observability Platform - AI-Powered Monitoring & Analytics | LogCrest',
    description: 'Complete observability platform for cloud-native applications. Monitor logs, metrics, traces, and infrastructure with AI-powered insights. Best observability platform for DevOps teams.',
    keywords: 'observability platform, system observability platform, cloud native observability, observability, full stack observability, application observability, infrastructure observability, best observability platform for startups',
    canonical: 'https://www.logcrest.com/observability-platform',
    ogType: 'website'
  },
  '/ai-monitoring': {
    title: 'AI Monitoring Tools - Intelligent Observability & Analytics | LogCrest',
    description: 'AI-powered monitoring tools for modern infrastructure. Intelligent log analysis, predictive alerting, and automated incident response. Advanced AI monitoring for cloud-native applications.',
    keywords: 'AI monitoring, AI observability tool, AI powered log analysis, AI monitoring tools, intelligent monitoring, AI-powered analytics, machine learning monitoring, predictive monitoring',
    canonical: 'https://www.logcrest.com/ai-monitoring',
    ogType: 'website'
  },
  '/cloud-monitoring': {
    title: 'Cloud Monitoring Solution - Multi-Cloud Infrastructure Monitoring | LogCrest',
    description: 'Comprehensive cloud monitoring solution for AWS, GCP, Azure, and hybrid environments. Real-time infrastructure monitoring, log aggregation, and performance analytics.',
    keywords: 'cloud monitoring solution, cloud monitoring, multi-cloud monitoring, AWS monitoring, GCP monitoring, Azure monitoring, cloud infrastructure monitoring, cloud native observability, cloud logs and tracing',
    canonical: 'https://www.logcrest.com/cloud-monitoring',
    ogType: 'website'
  },
  '/devops-observability': {
    title: 'DevOps Observability - Monitoring Tools for DevOps Teams | LogCrest',
    description: 'Complete DevOps observability platform with log management, infrastructure monitoring, and AI-powered insights. Best DevOps monitoring tools for modern teams.',
    keywords: 'DevOps observability, DevOps monitoring tools, DevOps platform, DevOps monitoring, SRE tools, site reliability engineering, DevOps analytics, CI/CD monitoring',
    canonical: 'https://www.logcrest.com/devops-observability',
    ogType: 'website'
  },
  '/logcrest-vs-datadog': {
    title: 'LogCrest vs Datadog - Open Source Alternative Comparison | LogCrest',
    description: 'Compare LogCrest vs Datadog. Discover why LogCrest is the best open source alternative to Datadog with better pricing, AI-powered insights, and superior log management.',
    keywords: 'LogCrest vs Datadog, Datadog alternative, open source alternative to datadog, datadog competitor, log management vs datadog, observability platform comparison',
    canonical: 'https://www.logcrest.com/logcrest-vs-datadog',
    ogType: 'website'
  },
  '/logcrest-vs-splunk': {
    title: 'LogCrest vs Splunk - Modern Log Management Alternative | LogCrest',
    description: 'Compare LogCrest vs Splunk. Modern, AI-powered log management platform with better pricing, real-time analytics, and cloud-native architecture. Best Splunk alternative.',
    keywords: 'LogCrest vs Splunk, Splunk alternative, splunk competitor, log management alternative, splunk vs logcrest, modern log management, AI-powered log analytics',
    canonical: 'https://www.logcrest.com/logcrest-vs-splunk',
    ogType: 'website'
  },
  '/logcrest-vs-newrelic': {
    title: 'LogCrest vs New Relic - APM & Observability Comparison | LogCrest',
    description: 'Compare LogCrest vs New Relic. AI-powered observability platform with comprehensive APM, log management, and infrastructure monitoring. Better pricing and features.',
    keywords: 'LogCrest vs New Relic, New Relic alternative, newrelic competitor, APM comparison, observability platform comparison, application performance monitoring alternative',
    canonical: 'https://www.logcrest.com/logcrest-vs-newrelic',
    ogType: 'website'
  },
  '/infrastructure-monitoring': {
    title: 'Infrastructure Monitoring - Server & Cloud Monitoring | LogCrest',
    description: 'Monitor your entire infrastructure with LogCrest. Server monitoring, cloud monitoring, and comprehensive infrastructure insights.',
    keywords: 'infrastructure monitoring, server monitoring, cloud monitoring, system monitoring, infrastructure insights',
    canonical: 'https://www.logcrest.com/infrastructure-monitoring',
    ogType: 'website'
  },
  '/rca-agent': {
    title: 'Root Cause Analysis Agent - AI-Powered Incident Investigation | LogCrest',
    description: 'Accelerate incident resolution with LogCrest\'s AI-powered Root Cause Analysis Agent. Automated investigation and intelligent insights.',
    keywords: 'root cause analysis, incident investigation, RCA agent, automated RCA, incident resolution, AI investigation',
    canonical: 'https://www.logcrest.com/rca-agent',
    ogType: 'website'
  },
  '/b2b-success-agent': {
    title: 'B2B Success Agent - Customer Success Automation | LogCrest',
    description: 'Enhance customer success with LogCrest\'s B2B Success Agent. Automated onboarding, health monitoring, and success insights.',
    keywords: 'B2B success agent, customer success, success automation, customer onboarding, success monitoring',
    canonical: 'https://www.logcrest.com/b2b-success-agent',
    ogType: 'website'
  },
  '/advanced-aiops': {
    title: 'Advanced AIOps - Next-Generation IT Operations | LogCrest',
    description: 'Experience next-generation AIOps with LogCrest\'s advanced platform. Predictive analytics, automated remediation, and intelligent operations.',
    keywords: 'advanced AIOps, next-gen AIOps, predictive analytics, automated remediation, intelligent operations, AI operations',
    canonical: 'https://www.logcrest.com/advanced-aiops',
    ogType: 'website'
  },
  '/agentic-database': {
    title: 'Agentic Database - AI-Powered Database Management | LogCrest',
    description: 'Optimize database performance with LogCrest\'s Agentic Database solution. Automated tuning, intelligent insights, and performance optimization.',
    keywords: 'agentic database, database management, database optimization, AI database, database performance, automated tuning',
    canonical: 'https://www.logcrest.com/agentic-database',
    ogType: 'website'
  },
  '/privacy': {
    title: 'Privacy Policy - LogCrest Data Protection & Privacy',
    description: 'Learn about LogCrest\'s commitment to data protection and privacy. Our comprehensive privacy policy and data handling practices.',
    keywords: 'privacy policy, data protection, privacy, GDPR, data handling, LogCrest privacy',
    canonical: 'https://www.logcrest.com/privacy',
    ogType: 'website'
  },
  '/terms': {
    title: 'Terms of Service - LogCrest Legal Terms & Conditions',
    description: 'Read LogCrest\'s terms of service and legal conditions. Understand your rights and responsibilities when using our platform.',
    keywords: 'terms of service, legal terms, conditions, LogCrest terms, service agreement',
    canonical: 'https://www.logcrest.com/terms',
    ogType: 'website'
  },
  '/faqs': {
    title: 'FAQs - Frequently Asked Questions | LogCrest',
    description: 'Find answers to common questions about LogCrest\'s AI-powered observability platform. Get help with setup, features, and troubleshooting.',
    keywords: 'FAQs, frequently asked questions, help, support, LogCrest help, observability FAQ',
    canonical: 'https://www.logcrest.com/faqs',
    ogType: 'website'
  }
};

class SEOService {
  constructor() {
    this.isProduction = process.env.NODE_ENV === 'production';
    this.baseUrl = this.isProduction ? SEO_CONFIG.baseUrl : 'http://localhost:3003';
  }

  /**
   * Update page SEO meta tags
   * @param {string} pathname - Current page path
   * @param {Object} customData - Custom SEO data to override defaults
   */
  updatePageSEO(pathname, customData = {}) {
    if (!this.isProduction) {
      console.log('SEO Service: Development mode - SEO updates disabled');
      return;
    }

    const seoData = { ...PAGE_SEO_DATA[pathname], ...customData };
    
    if (!seoData) {
      console.warn(`No SEO data found for path: ${pathname}`);
      return;
    }

    this.updateMetaTags(seoData);
    this.updateStructuredData(seoData);
    this.updateCanonicalURL(seoData.canonical);
  }

  /**
   * Update HTML meta tags
   * @param {Object} seoData - SEO data object
   */
  updateMetaTags(seoData) {
    // Update title
    if (seoData.title) {
      document.title = seoData.title;
    }

    // Update meta description
    this.updateMetaTag('description', seoData.description);
    
    // Update meta keywords
    this.updateMetaTag('keywords', seoData.keywords);

    // Open Graph tags
    this.updateMetaTag('og:title', seoData.title, 'property');
    this.updateMetaTag('og:description', seoData.description, 'property');
    this.updateMetaTag('og:type', seoData.ogType || 'website', 'property');
    this.updateMetaTag('og:url', seoData.canonical, 'property');
    this.updateMetaTag('og:site_name', SEO_CONFIG.siteName, 'property');
    this.updateMetaTag('og:image', `${this.baseUrl}/og-image.png`, 'property');
    this.updateMetaTag('og:image:width', '1200', 'property');
    this.updateMetaTag('og:image:height', '630', 'property');

    // Twitter Card tags
    this.updateMetaTag('twitter:card', 'summary_large_image');
    this.updateMetaTag('twitter:site', SEO_CONFIG.twitterHandle);
    this.updateMetaTag('twitter:title', seoData.title);
    this.updateMetaTag('twitter:description', seoData.description);
    this.updateMetaTag('twitter:image', `${this.baseUrl}/twitter-image.png`);

    // Additional meta tags
    this.updateMetaTag('author', SEO_CONFIG.author);
    this.updateMetaTag('robots', 'index, follow');
    this.updateMetaTag('googlebot', 'index, follow');
    this.updateMetaTag('bingbot', 'index, follow');
  }

  /**
   * Update or create meta tag
   * @param {string} name - Meta tag name
   * @param {string} content - Meta tag content
   * @param {string} attribute - Attribute type (name or property)
   */
  updateMetaTag(name, content, attribute = 'name') {
    if (!content) return;

    let metaTag = document.querySelector(`meta[${attribute}="${name}"]`);
    
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.setAttribute(attribute, name);
      document.head.appendChild(metaTag);
    }
    
    metaTag.setAttribute('content', content);
  }

  /**
   * Update structured data (JSON-LD)
   * @param {Object} seoData - SEO data object
   */
  updateStructuredData(seoData) {
    // Remove existing structured data
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    if (seoData.structuredData) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(seoData.structuredData);
      document.head.appendChild(script);
    }
  }

  /**
   * Update canonical URL
   * @param {string} canonicalUrl - Canonical URL
   */
  updateCanonicalURL(canonicalUrl) {
    if (!canonicalUrl) return;

    let canonicalLink = document.querySelector('link[rel="canonical"]');
    
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    
    canonicalLink.href = canonicalUrl;
  }

  /**
   * Generate sitemap data
   * @returns {Array} Array of sitemap entries
   */
  generateSitemapData() {
    const sitemapEntries = [];
    
    Object.keys(PAGE_SEO_DATA).forEach(path => {
      sitemapEntries.push({
        url: `${this.baseUrl}${path}`,
        lastmod: new Date().toISOString().split('T')[0],
        changefreq: path === '/' ? 'daily' : 'weekly',
        priority: path === '/' ? '1.0' : '0.8'
      });
    });

    return sitemapEntries;
  }

  /**
   * Track page view for SEO analytics
   * @param {string} pathname - Current page path
   * @param {Object} additionalData - Additional tracking data
   */
  trackPageView(pathname, additionalData = {}) {
    if (!this.isProduction) return;

    // Google Analytics 4
    if (typeof window.gtag !== 'undefined') {
      window.gtag('config', SEO_CONFIG.googleAnalyticsId, {
        page_title: PAGE_SEO_DATA[pathname]?.title || SEO_CONFIG.defaultTitle,
        page_location: `${this.baseUrl}${pathname}`,
        ...additionalData
      });
    }

    // Google Tag Manager
    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push({
        event: 'page_view',
        page_title: PAGE_SEO_DATA[pathname]?.title || SEO_CONFIG.defaultTitle,
        page_location: `${this.baseUrl}${pathname}`,
        ...additionalData
      });
    }
  }

  /**
   * Get SEO data for a specific page
   * @param {string} pathname - Page path
   * @returns {Object} SEO data object
   */
  getPageSEOData(pathname) {
    return PAGE_SEO_DATA[pathname] || {
      title: SEO_CONFIG.defaultTitle,
      description: SEO_CONFIG.defaultDescription,
      keywords: SEO_CONFIG.defaultKeywords,
      canonical: `${this.baseUrl}${pathname}`
    };
  }
}

// Create singleton instance
const seoService = new SEOService();

export default seoService;
export { SEO_CONFIG, PAGE_SEO_DATA };
